"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "@tanstack/react-router";
import {
  IconArrowLeft,
  IconLoader2,
  IconUserPlus,
  IconMail,
  IconMapPin,
  IconBuilding,
  IconDotsVertical,
  IconPhone,
  IconSearch,
  IconTrash,
  IconLink,
  IconUserCog,
  IconMailForward,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  getVenueById,
  getVenueUsers,
  updateVenueUser,
  deleteVenueUser,
  reinviteVenueUser,
} from "@/services/venue";
import {
  OrganizationalRole,
  Venue,
  VenueUser,
  VenueUserRole,
} from "@/types";
import { InviteUserDialog } from "@/features/venue/components";
import {
  ORGANIZATIONAL_ROLE_OPTIONS,
  organizationalRolesForVenueRole,
} from "@/features/venue/organizational-roles";
import { toast } from "sonner";
import { formatPhoneNumber } from "@/lib/utils";

interface VenueDetailClientProps {
  venueId: string;
}

export default function VenueDetailClient({ venueId }: VenueDetailClientProps) {
  const router = useRouter();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [users, setUsers] = useState<VenueUser[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [changeRoleDialogOpen, setChangeRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<VenueUser | null>(null);
  const [selectedRole, setSelectedRole] = useState<VenueUserRole>(
    VenueUserRole.MEMBER
  );
  const [selectedOrganizationalRoles, setSelectedOrganizationalRoles] =
    useState<OrganizationalRole[]>([]);
  const [organizationalRolesError, setOrganizationalRolesError] = useState<
    string | null
  >(null);
  // Changing Role re-derives the organizational roles, but must not undo a
  // choice made by hand while the user is a Member.
  const organizationalRolesTouchedRef = useRef(false);
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [removeUserDialogOpen, setRemoveUserDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<VenueUser | null>(null);
  const [isRemovingUser, setIsRemovingUser] = useState(false);
  const [reinvitingUserId, setReinvitingUserId] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!venueId) return;
    try {
      setIsLoadingUsers(true);
      const usersResponse = await getVenueUsers(venueId, {
        pageNumber: pageIndex + 1,
        pageSize: pageSize,
        search: debouncedSearch || undefined,
      });
      setUsers(usersResponse.data);
      setTotalUsers(usersResponse.count);
    } catch (userErr) {
      console.error("Error fetching venue users:", userErr);
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [venueId, pageIndex, pageSize, debouncedSearch]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPageIndex(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch venue details on mount
  useEffect(() => {
    const fetchVenue = async () => {
      if (!venueId) {
        setError("Venue ID is required");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const venueData = await getVenueById(venueId);
        setVenue(venueData);
      } catch (err) {
        console.error("Error fetching venue:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load venue data"
        );
        toast.error("Failed to load venue details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchVenue();
  }, [venueId]);

  // Fetch users when page changes
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Return to the venues list. Prefer browser history so the list restores the
  // exact page/search the user came from; fall back to a plain navigate when
  // there is no in-app history (e.g. the detail page was opened directly).
  const handleBackToVenues = useCallback(() => {
    if (window.history.length > 1) {
      router.history.back();
    } else {
      router.navigate({
        to: "/venues",
        search: { page: 1, pageSize: 10, search: "" },
      });
    }
  }, [router]);

  const handleInviteSuccess = () => {
    if (pageIndex === 0) {
      fetchUsers();
    } else {
      setPageIndex(0);
    }
  };

  const handleOpenChangeRoleDialog = (user: VenueUser) => {
    setSelectedUser(user);
    setSelectedRole(user.venueUserRole);
    // The stored values win. An admin with nothing stored starts from the
    // default both.
    setSelectedOrganizationalRoles(
      user.organizationalRoles?.length
        ? user.organizationalRoles
        : organizationalRolesForVenueRole(user.venueUserRole)
    );
    setOrganizationalRolesError(null);
    organizationalRolesTouchedRef.current = false;
    setChangeRoleDialogOpen(true);
  };

  const handleSelectedRoleChange = (role: VenueUserRole) => {
    setSelectedRole(role);
    // Choosing Admin selects both, overwriting a choice made while the user
    // was a Member. Only a Member keeps what was picked by hand.
    if (role === VenueUserRole.ADMIN) {
      organizationalRolesTouchedRef.current = false;
    } else if (organizationalRolesTouchedRef.current) {
      return;
    }
    setSelectedOrganizationalRoles(organizationalRolesForVenueRole(role));
    setOrganizationalRolesError(null);
  };

  const handleOrganizationalRolesChange = (value: string[]) => {
    organizationalRolesTouchedRef.current = true;
    setSelectedOrganizationalRoles(value as OrganizationalRole[]);
    if (value.length > 0) setOrganizationalRolesError(null);
  };

  const handleChangeRole = async () => {
    if (!selectedUser || !venueId) return;

    if (selectedOrganizationalRoles.length === 0) {
      setOrganizationalRolesError("Select at least one organizational role");
      return;
    }

    try {
      setIsChangingRole(true);
      await updateVenueUser(venueId, selectedUser.user.id, {
        venueUserRole: selectedRole,
        organizationalRoles: selectedOrganizationalRoles,
      });
      toast.success("User role updated successfully");

      await fetchUsers();

      setChangeRoleDialogOpen(false);
    } catch (err) {
      console.error("Error updating user role:", err);
      toast.error("Failed to update user role");
    } finally {
      setIsChangingRole(false);
    }
  };

  const handleReinviteUser = async (user: VenueUser) => {
    if (!venueId) return;

    try {
      setReinvitingUserId(user.user.id);
      await reinviteVenueUser(venueId, user.user.id);
      toast.success(`Invitation re-sent to ${user.user.email}`);
      await fetchUsers();
    } catch (err) {
      console.error("Error re-inviting user:", err);
      toast.error("Failed to re-invite user");
    } finally {
      setReinvitingUserId(null);
    }
  };

  const handleOpenRemoveUserDialog = (user: VenueUser) => {
    setUserToRemove(user);
    setRemoveUserDialogOpen(true);
  };

  const handleRemoveUser = async () => {
    if (!userToRemove || !venueId) return;

    try {
      setIsRemovingUser(true);
      await deleteVenueUser(venueId, userToRemove.user.id);
      toast.success("User removed from venue");

      if (users.length === 1 && pageIndex > 0) {
        setPageIndex((prev) => prev - 1);
      } else {
        await fetchUsers();
      }

      setRemoveUserDialogOpen(false);
      setUserToRemove(null);
    } catch (err) {
      console.error("Error removing user:", err);
      toast.error("Failed to remove user from venue");
    } finally {
      setIsRemovingUser(false);
    }
  };

  const getUserDisplayName = (user: VenueUser): string => {
    const firstName = user.user.firstName;
    const lastName = user.user.lastName;

    if (!firstName && !lastName) {
      return "-";
    }

    return [firstName, lastName].filter(Boolean).join(" ");
  };

  const getUserDisplayNameOrEmail = (user: VenueUser): string => {
    return user.user.firstName && user.user.lastName
      ? getUserDisplayName(user)
      : user.user.email;
  };

  const getStatusBadge = (status: VenueUser["status"]) => {
    const variants = {
      invited: "secondary",
      active: "default",
      inactive: "outline",
    } as const;

    return (
      <Badge variant={variants[status]} className="capitalize">
        {status}
      </Badge>
    );
  };

  const getRoleLabel = (role: VenueUserRole): string => {
    return role === VenueUserRole.ADMIN ? "Admin" : "Member";
  };

  // Mirrors the Change Role dialog: the stored values win, and an admin with
  // nothing stored reads as the default both.
  const getOrganizationalRoleLabels = (user: VenueUser): string[] => {
    const roles = user.organizationalRoles?.length
      ? user.organizationalRoles
      : organizationalRolesForVenueRole(user.venueUserRole);
    return ORGANIZATIONAL_ROLE_OPTIONS.filter((option) =>
      roles.includes(option.value)
    ).map((option) => option.label);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <IconLoader2 className="size-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading venue details...</p>
        </div>
      </div>
    );
  }

  if (error || !venue) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
            <h2 className="mb-2 text-lg font-semibold text-destructive">
              Error
            </h2>
            <p className="text-sm text-destructive">
              {error || "Venue not found"}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleBackToVenues}
            >
              Back to Venues
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col gap-6 p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToVenues}
              className="shrink-0"
            >
              <IconArrowLeft className="size-5" />
              <span className="sr-only">Back to venues</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                {venue.name}
              </h1>
              <p className="text-muted-foreground mt-1">
                View and manage venue details and users
              </p>
            </div>
          </div>
          <InviteUserDialog
            venueId={venueId}
            venueName={venue.name}
            onSuccess={handleInviteSuccess}
          >
            <Button className="gap-2">
              <IconUserPlus className="size-4" />
              Invite User
            </Button>
          </InviteUserDialog>
        </div>

        {/* Venue Details */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Organisation */}
                {venue.organisation && (
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-500/10">
                      <IconBuilding className="size-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                        Organisation
                      </p>
                      <p className="text-sm font-semibold">
                        {venue.organisation.name}
                      </p>
                    </div>
                  </div>
                )}

                {/* Phone Number */}
                {venue.phoneNumber && (
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-green-500/10">
                      <IconPhone className="size-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                        Phone Number
                      </p>
                      <p className="text-sm font-semibold">
                        {formatPhoneNumber(venue.phoneNumber)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Email */}
                {venue.email && (
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-purple-500/10">
                      <IconMail className="size-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                        Email
                      </p>
                      <p className="text-sm font-semibold break-all">
                        {venue.email}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Address */}
                {venue.address && (
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-orange-500/10">
                      <IconMapPin className="size-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                        Address
                      </p>
                      <div className="text-sm font-semibold space-y-0.5">
                        <p>{venue.address.addressLine1}</p>
                        {venue.address.addressLine2 && (
                          <p>{venue.address.addressLine2}</p>
                        )}
                        <p>
                          {venue.address.city}, {venue.address.state}{" "}
                          {venue.address.zipcode}
                        </p>
                        <p className="text-muted-foreground">
                          {venue.address.country}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Slug */}
                {venue.slug && (
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-cyan-500/10">
                      <IconLink className="size-5 text-cyan-600 dark:text-cyan-400" />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                        Slug
                      </p>
                      <p className="text-sm font-semibold break-all">
                        {venue.slug}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <CardTitle>Venue Users</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-semibold">
                    {totalUsers} Total
                  </Badge>
                </div>
              </div>
              <div className="relative w-full max-w-xs">
                <IconSearch className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {users.length === 0 &&
            !isLoadingUsers &&
            totalUsers === 0 &&
            !debouncedSearch ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12">
                <p className="text-muted-foreground mb-4">
                  No users found for this venue
                </p>
                <InviteUserDialog
                  venueId={venueId}
                  venueName={venue.name}
                  onSuccess={handleInviteSuccess}
                >
                  <Button variant="outline">
                    <IconUserPlus className="mr-2 size-4" />
                    Invite First User
                  </Button>
                </InviteUserDialog>
              </div>
            ) : (
              <>
                <div className="overflow-hidden rounded-lg border">
                  <Table>
                    <TableHeader className="bg-muted">
                      <TableRow>
                        <TableHead className="pl-6">Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Organizational Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[50px] pr-6">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoadingUsers ? (
                        <TableRow>
                          <TableCell colSpan={6} className="h-24 text-center">
                            <div className="flex items-center justify-center">
                              <IconLoader2 className="mr-2 size-4 animate-spin" />
                              <span className="text-muted-foreground">
                                Loading users...
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : users.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="h-24 text-center text-muted-foreground"
                          >
                            No users found matching your search.
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((venueUser) => (
                          <TableRow key={venueUser.id}>
                            <TableCell className="font-medium pl-6">
                              {getUserDisplayName(venueUser)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {venueUser.user.email}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {getRoleLabel(venueUser.venueUserRole)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {getOrganizationalRoleLabels(venueUser).length >
                              0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {getOrganizationalRoleLabels(venueUser).map(
                                    (label) => (
                                      <Badge key={label} variant="outline">
                                        {label}
                                      </Badge>
                                    )
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(venueUser.status)}
                            </TableCell>
                            <TableCell className="pr-6">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <IconDotsVertical className="size-4" />
                                    <span className="sr-only">Open menu</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleOpenChangeRoleDialog(venueUser)
                                    }
                                  >
                                    <IconUserCog className="mr-2 size-4" />
                                    Change Role
                                  </DropdownMenuItem>
                                  {venueUser.status === "invited" && (
                                    <DropdownMenuItem
                                      disabled={
                                        reinvitingUserId === venueUser.user.id
                                      }
                                      onClick={() =>
                                        handleReinviteUser(venueUser)
                                      }
                                    >
                                      <IconMailForward className="mr-2 size-4" />
                                      Resend Invite
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() =>
                                      handleOpenRemoveUserDialog(venueUser)
                                    }
                                  >
                                    <IconTrash className="mr-2 size-4" />
                                    Remove User
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {totalUsers > 0 && (
                  <div className="flex items-center justify-between px-2 mt-4">
                    <div className="text-muted-foreground flex-1 text-sm">
                      Showing {users.length > 0 ? pageIndex * pageSize + 1 : 0}{" "}
                      to {Math.min((pageIndex + 1) * pageSize, totalUsers)} of{" "}
                      {totalUsers} users
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPageIndex((prev) => prev - 1)}
                        disabled={pageIndex === 0 || isLoadingUsers}
                      >
                        Previous
                      </Button>
                      <div className="text-sm font-medium">
                        Page {pageIndex + 1} of{" "}
                        {Math.max(1, Math.ceil(totalUsers / pageSize))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPageIndex((prev) => prev + 1)}
                        disabled={
                          pageIndex >= Math.ceil(totalUsers / pageSize) - 1 ||
                          isLoadingUsers
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Change Role Dialog */}
        <Dialog
          open={changeRoleDialogOpen}
          onOpenChange={setChangeRoleDialogOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change User Role</DialogTitle>
              <DialogDescription>
                Update the role and organizational role for{" "}
                {selectedUser && getUserDisplayNameOrEmail(selectedUser)}.
                Admins always cover both Sales and Marketing; choose which a
                Member works in.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-4">
                <label
                  htmlFor="change-role-select"
                  className="text-sm font-medium mb-3 block"
                >
                  Select Role
                </label>
                <Select
                  value={selectedRole}
                  onValueChange={(value) =>
                    handleSelectedRoleChange(value as VenueUserRole)
                  }
                  disabled={isChangingRole}
                >
                  <SelectTrigger id="change-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={VenueUserRole.ADMIN}>Admin</SelectItem>
                    <SelectItem value={VenueUserRole.MEMBER}>Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-4">
                <label
                  htmlFor="change-organizational-roles"
                  className="text-sm font-medium mb-3 block"
                >
                  Organizational Role
                </label>
                <MultiSelect
                  id="change-organizational-roles"
                  value={selectedOrganizationalRoles}
                  onValueChange={handleOrganizationalRolesChange}
                  options={ORGANIZATIONAL_ROLE_OPTIONS}
                  placeholder="Select at least one"
                  aria-describedby="change-organizational-roles-error"
                  invalid={!!organizationalRolesError}
                  required
                  // Editable only for a Member; Admin is locked to both.
                  disabled={isChangingRole}
                />
                {organizationalRolesError && (
                  <p
                    id="change-organizational-roles-error"
                    className="text-sm text-destructive"
                  >
                    {organizationalRolesError}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setChangeRoleDialogOpen(false)}
                disabled={isChangingRole}
              >
                Cancel
              </Button>
              <Button onClick={handleChangeRole} disabled={isChangingRole}>
                {isChangingRole ? (
                  <>
                    <IconLoader2 className="mr-2 size-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Role"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove User Dialog */}
        <Dialog
          open={removeUserDialogOpen}
          onOpenChange={(open) => {
            if (!open) setUserToRemove(null);
            setRemoveUserDialogOpen(open);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove User from Venue</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove{" "}
                {userToRemove && getUserDisplayNameOrEmail(userToRemove)} from
                this venue? They will lose access to the venue.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRemoveUserDialogOpen(false)}
                disabled={isRemovingUser}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRemoveUser}
                disabled={isRemovingUser}
              >
                {isRemovingUser ? (
                  <>
                    <IconLoader2 className="mr-2 size-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  "Remove User"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
